import { Box, Typography, Divider, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import { CustomerTypes } from '../types';

interface Props {
    values: Partial<CustomerTypes.OnboardingForm>;
}

type CardStatus = 'success' | 'failure' | 'disabled' | 'userAction';

interface FlowCard {
    kicker: string;
    title: string;
    description: string;
    status: CardStatus;
    titleIcon?: boolean; // green checkmark next to the title (User checks out)
}

const NOTIFICATION_LABEL: Record<string, string> = {
    NONE: 'Notifications off',
    EXPIRED: 'On expiration',
    EXPIRED_ESCALATED: 'On expire & escalate',
    ESCALATED: 'On escalation',
    ALL: 'All events',
};

interface ThemeColors {
    successMain: string;
    failureMain: string;
}

const statusVisual = (status: CardStatus, colors: ThemeColors) => {
    switch (status) {
        case 'success':
            return {
                bg: alpha(colors.successMain, 0.08),
                border: colors.successMain,
                borderStyle: 'solid' as const,
                titleColor: 'text.primary',
                kickerColor: 'text.secondary',
                descColor: 'text.secondary',
                dot: colors.successMain,
                opacity: 1,
            };
        case 'failure':
            return {
                bg: alpha(colors.failureMain, 0.08),
                border: colors.failureMain,
                borderStyle: 'solid' as const,
                titleColor: 'text.primary',
                kickerColor: 'text.secondary',
                descColor: 'text.secondary',
                dot: colors.failureMain,
                opacity: 1,
            };
        case 'disabled':
            return {
                bg: 'transparent',
                border: 'rgba(0,0,0,0.18)',
                borderStyle: 'dashed' as const,
                titleColor: 'text.disabled',
                kickerColor: 'text.disabled',
                descColor: 'text.disabled',
                dot: 'rgba(0,0,0,0.28)',
                opacity: 0.85,
            };
        case 'userAction':
            return {
                bg: 'transparent',
                border: 'rgba(0,0,0,0.22)',
                borderStyle: 'dashed' as const,
                titleColor: 'text.primary',
                kickerColor: 'text.secondary',
                descColor: 'text.secondary',
                dot: null,
                opacity: 1,
            };
    }
};

const Card: React.FC<{ card: FlowCard; colors: ThemeColors }> = ({ card, colors }) => {
    const v = statusVisual(card.status, colors);
    return (
        <Box
            sx={{
                position: 'relative',
                border: `1.5px ${v.borderStyle} ${v.border}`,
                bgcolor: v.bg,
                borderRadius: 2,
                px: 2,
                py: 1.5,
                width: '100%',
                height: 130,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                opacity: v.opacity,
            }}
        >
            <Typography
                sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    color: v.kickerColor,
                    textTransform: 'uppercase',
                    lineHeight: 1.2,
                }}
            >
                {card.kicker}
            </Typography>
            {v.dot && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: v.dot,
                    }}
                />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                {card.titleIcon && (
                    <CheckIcon sx={{ fontSize: 16, color: colors.successMain }} />
                )}
                <Typography
                    sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: v.titleColor,
                        lineHeight: 1.2,
                    }}
                >
                    {card.title}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontSize: 12,
                    color: v.descColor,
                    lineHeight: 1.3,
                    mt: 'auto',
                }}
            >
                {card.description}
            </Typography>
        </Box>
    );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
    </Box>
);

const BranchHeader: React.FC<{
    icon: React.ReactNode;
    label: string;
    color: string;
}> = ({ icon, label, color }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
            <Box sx={{ color, display: 'flex' }}>{icon}</Box>
            <Typography
                sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </Typography>
        </Box>
        <Divider sx={{ flex: 1, borderColor: color, opacity: 0.35 }} />
    </Box>
);

const Branch: React.FC<{ cards: FlowCard[]; arrowColor: string; colors: ThemeColors }> = ({
    cards,
    arrowColor,
    colors,
}) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            columnGap: '36px',
            rowGap: 0,
            position: 'relative',
        }}
    >
        {cards.map((card, i) => (
            <Box key={i} sx={{ position: 'relative', display: 'flex' }}>
                <Card card={card} colors={colors} />
                {i < cards.length - 1 && (
                    <ArrowForwardIcon
                        sx={{
                            position: 'absolute',
                            right: -28,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: arrowColor,
                            fontSize: 20,
                        }}
                    />
                )}
            </Box>
        ))}
    </Box>
);

export const CheckOnMeFlowDiagram: React.FC<Props> = ({ values }) => {
    const theme = useTheme();
    const colors: ThemeColors = {
        successMain: theme.palette.success.main,
        failureMain: theme.palette.primary.main,
    };

    const enabled = values?.checkOnMeConfig === 'Enabled';
    const reminderOn = values?.checkoutReminderMessage === true;
    const notifyMethod = values?.expireNotifyMethod === 'CALL' ? 'CALL' : 'MESSAGE';
    const escalate = values?.escalateCheckOnMe === true;
    const dispatchEnabled = values?.checkOnMeDispatchEnabled === true;
    const escalateTimeout = values?.escalateCheckOnMeTimeout ?? 1;
    const notifications = values?.checkOnMeNotifications ?? 'EXPIRED_ESCALATED';
    const riskSurvey = values?.checkOnMeRiskReportSurvey === true;

    // When the feature is disabled, gray everything out by forcing all settings-driven
    // statuses to "disabled". Permanent steps (Session Start, User checks out) stay
    // visible but lose their accent color so the whole diagram reads as inactive.
    const off = !enabled;

    const successBranch: FlowCard[] = [
        {
            kicker: 'SESSION START',
            title: 'Check On Me starts',
            description: 'User schedules a session',
            status: off ? 'disabled' : 'success',
        },
        {
            kicker: '2 MIN BEFORE',
            title: 'Check Out Reminder',
            description: 'Pre-expire push or SMS',
            status: off || !reminderOn ? 'disabled' : 'success',
        },
        {
            kicker: 'USER ACTION',
            title: 'User checks out',
            description: "Confirms they're safe",
            status: off ? 'disabled' : 'userAction',
            titleIcon: !off,
        },
        {
            kicker: 'POST-CHECKOUT',
            title: 'Risk Survey',
            description: 'Asks about session safety',
            status: off || !riskSurvey ? 'disabled' : 'success',
        },
    ];

    const expirationCard: FlowCard = {
        kicker: 'AT EXPIRATION',
        title: notifyMethod === 'CALL' ? 'Expiration call' : 'Expiration message',
        description: `Method: ${notifyMethod === 'CALL' ? 'Call' : 'Message'}`,
        status: off ? 'disabled' : 'failure',
    };

    const graceCard: FlowCard = {
        kicker: escalate ? `+${escalateTimeout} MIN` : 'NO BUFFER',
        title: 'Grace period',
        description: escalate ? `${escalateTimeout} min buffer` : 'Escalation off',
        status: off || !escalate ? 'disabled' : 'failure',
    };

    const escalateDispatchActive = !off && escalate && dispatchEnabled;
    const escalateCard: FlowCard = {
        kicker: 'ESCALATION',
        title: 'Escalate to Dispatch',
        description: !escalate
            ? 'Escalation off'
            : !dispatchEnabled
                ? 'Dispatch off'
                : 'POM Dispatch alerted',
        status: escalateDispatchActive ? 'failure' : 'disabled',
    };

    const notifyActive = !off && notifications !== 'NONE';
    const notifyCard: FlowCard = {
        kicker: 'NOTIFICATION',
        title: 'Notify Team Leaders',
        description: NOTIFICATION_LABEL[notifications] ?? notifications,
        status: notifyActive ? 'failure' : 'disabled',
    };

    const failureBranch: FlowCard[] = [expirationCard, graceCard, escalateCard, notifyCard];

    const successArrowColor = off ? 'rgba(0,0,0,0.18)' : colors.successMain;
    const failureArrowColor = off ? 'rgba(0,0,0,0.18)' : colors.failureMain;

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 1100,
                p: 3,
                borderRadius: 2,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                mb: 2,
            }}
        >
            {/* Header bar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                }}
            >
                <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    Workflow {off ? '(disabled)' : ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <LegendDot color={colors.failureMain} label="Enabled" />
                    <LegendDot color="rgba(0,0,0,0.28)" label="Disabled" />
                    <LegendDot color={colors.successMain} label="Always on" />
                </Box>
            </Box>

            {/* Success branch */}
            <BranchHeader
                icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
                label="If user checks out"
                color={colors.successMain}
            />
            <Branch cards={successBranch} arrowColor={successArrowColor} colors={colors} />

            {/* Failure branch */}
            <Box sx={{ mt: 3.5 }}>
                <BranchHeader
                    icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
                    label="If user does not check out"
                    color={colors.failureMain}
                />
                <Branch cards={failureBranch} arrowColor={failureArrowColor} colors={colors} />
            </Box>
        </Box>
    );
};
